import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import {
  LEVEL_HEIGHT,
  TILE_SIZE,
  pieceOrigin,
  type ArenaMap,
  type PieceId,
  type PlacedPiece,
} from '@arena/shared';
import { Environment, FLOORS, createRenderer } from '../lib/environment';
import {
  buildMapObject,
  buildTerrain,
  loadPieceTemplates,
  paintGround,
  placePiece,
  type PieceTemplates,
} from '../lib/assets';

export type TerrainBrush =
  'raise' | 'lower' | 'flatten' | 'set' | 'smooth' | 'erase';
/** Brushes that keep working while the mouse is held still. */
const BUILDING_BRUSHES: TerrainBrush[] = ['raise', 'lower', 'smooth'];
/** How often a held brush re-applies, in milliseconds. */
const BRUSH_INTERVAL = 100;

export type Tool =
  | { kind: 'piece'; piece: PieceId }
  | { kind: 'spawn'; team: 0 | 1 }
  | { kind: 'dummy' }
  | { kind: 'terrain' }
  | { kind: 'select' }
  | { kind: 'erase' };

type Picked = { piece: number } | { spawn: number } | { dummy: number };

const MAX_SPAWNS_PER_TEAM = 3;
/** `arena.test.ts` holds every arena to this many. */
export const MAX_DUMMIES = 4;
const MAX_UNDO = 100;
const TEAM_COLORS = [0x3a8bff, 0xff4a3a];
const DUMMY_COLOR = 0xe6c06a;
/** Held keys that pan the camera over the ground: [right, forward] relative to the view. */
const PAN_KEYS: Record<string, [number, number]> = {
  ArrowUp: [0, 1],
  KeyW: [0, 1],
  ArrowDown: [0, -1],
  KeyS: [0, -1],
  ArrowLeft: [-1, 0],
  KeyA: [-1, 0],
  ArrowRight: [1, 0],
  KeyD: [1, 0],
};
/** Pan speed as a fraction of the camera's distance to its target, per second. */
const PAN_RATE = 0.8;

/** Keys typed into a field belong to the field, not the editor. */
const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLInputElement ||
  target instanceof HTMLTextAreaElement ||
  target instanceof HTMLSelectElement ||
  (target instanceof HTMLElement && target.isContentEditable);

/** Three.js side of the map editor: draws the map, a ghost of the next placement, and edits. */
export class EditorEngine {
  map: ArenaMap = { name: 'Untitled', pieces: [], spawns: [] };
  tool: Tool = { kind: 'piece', piece: 'floor' };
  level = 0;
  rot = 0;
  scale = 1;
  /** Index into `map.pieces` of the piece the select tool picked. */
  selected: number | null = null;
  brush: TerrainBrush = 'raise';
  /** Corners out from the centre the brush reaches; 0 is one corner. */
  radius = 1;
  /** Levels a raise or lower adds per application at the brush centre. */
  strength = 0.5;
  private stroke: {
    brush: TerrainBrush;
    flatten: number;
    last: string;
    time: number;
  } | null = null;
  private hoverY = 0;
  private cornerMarkers = new THREE.Points(
    new THREE.BufferGeometry(),
    new THREE.PointsMaterial({ color: 0xfff1a8, size: 0.35 }),
  );
  private highlight = new THREE.BoxHelper(new THREE.Object3D(), 0xffd34a);
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(55, 1, 0.1, 500);
  private controls: OrbitControls;
  private templates: PieceTemplates | null = null;
  private environment: Environment | null = null;
  private mapObject = new THREE.Group();
  private spawnObject = new THREE.Group();
  private ghost: THREE.Object3D | null = null;
  // Odd division count so lines fall on cell edges; cells are centered on multiples of TILE_SIZE.
  private grid = new THREE.GridHelper(41 * TILE_SIZE, 41, 0xe6c06a, 0x555555);
  private hover: { x: number; z: number } | null = null;
  private undoStack: string[] = [];
  private running = true;
  private disposers: (() => void)[] = [];
  private held = new Set<string>();

  constructor(
    private canvas: HTMLCanvasElement,
    private onChange: () => void,
  ) {
    this.renderer = createRenderer(canvas);
    this.camera.position.set(30, 40, 40);
    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.mouseButtons = {
      LEFT: null,
      MIDDLE: THREE.MOUSE.PAN,
      RIGHT: THREE.MOUSE.ROTATE,
    };
    this.controls.screenSpacePanning = false;
    this.controls.zoomToCursor = true;
    this.controls.target.set(0, 0, 0);
    this.highlight.visible = false;
    this.cornerMarkers.visible = false;
    this.scene.add(
      this.mapObject,
      this.spawnObject,
      this.grid,
      this.highlight,
      this.cornerMarkers,
    );
  }

  async start(): Promise<void> {
    this.templates = await loadPieceTemplates();
    if (!this.running) return;
    this.rebuild();
    this.refreshGhost();

    const listen = <K extends keyof WindowEventMap>(
      target: Window | HTMLElement,
      type: K,
      handler: (e: WindowEventMap[K]) => void,
    ) => {
      target.addEventListener(type, handler as EventListener);
      this.disposers.push(() =>
        target.removeEventListener(type, handler as EventListener),
      );
    };
    listen(this.canvas, 'pointermove', (e) => {
      this.updateHover(e);
      const hover = this.hover;
      if (this.stroke && hover && `${hover.x},${hover.z}` !== this.stroke.last)
        this.paint();
    });
    listen(this.canvas, 'pointerdown', (e) => {
      // Leave any field or button, so keys go to the editor (and a pending field edit commits).
      if (document.activeElement instanceof HTMLElement)
        document.activeElement.blur();
      if (e.button !== 0) return;
      this.updateHover(e);
      if (this.tool.kind === 'select') this.select(this.pick(e));
      else if (this.tool.kind === 'terrain') this.startStroke(e.shiftKey);
      else this.apply(e.shiftKey, e);
    });
    listen(window, 'pointerup', () => (this.stroke = null));
    listen(this.canvas, 'contextmenu', (e) => e.preventDefault());
    listen(window, 'keydown', (e) => {
      if (isTyping(e.target)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) {
        if (e.code === 'KeyZ' && !e.altKey) this.undo();
        return;
      }
      if (e.code in PAN_KEYS) {
        // Otherwise arrows also scroll the panel or page.
        e.preventDefault();
        this.held.add(e.code);
        return;
      }
      if (e.code === 'KeyR') this.rotate(e.shiftKey);
      if (e.code === 'Minus') this.setScale(this.scale - 0.25);
      if (e.code === 'Equal') this.setScale(this.scale + 0.25);
      if (e.code === 'BracketRight' || e.code === 'PageUp')
        this.setLevel(this.level + 1);
      if (e.code === 'BracketLeft' || e.code === 'PageDown')
        this.setLevel(this.level - 1);
      if (e.code === 'Delete' || e.code === 'Backspace') this.deleteSelected();
      if (e.code === 'Escape') this.select(null);
    });
    listen(window, 'keyup', (e) => this.held.delete(e.code));
    listen(window, 'blur', () => this.held.clear());
    const resize = new ResizeObserver(() => this.resize());
    resize.observe(this.canvas.parentElement!);
    this.disposers.push(() => resize.disconnect());
    this.resize();
    let last = performance.now();
    const loop = (now = performance.now()) => {
      if (!this.running) return;
      this.pan(Math.min(0.1, (now - last) / 1000));
      last = now;
      const stroke = this.stroke;
      if (
        stroke &&
        BUILDING_BRUSHES.includes(stroke.brush) &&
        now - stroke.time > BRUSH_INTERVAL
      )
        this.paint();
      this.controls.update();
      this.environment?.update(this.camera, this.controls.target);
      this.renderer.render(this.scene, this.camera);
      requestAnimationFrame(loop);
    };
    loop();
  }

  /** Slides the camera and its target over the ground, along the view's heading. */
  private pan(dt: number): void {
    let right = 0;
    let forward = 0;
    for (const code of this.held) {
      const [r, f] = PAN_KEYS[code] ?? [0, 0];
      right += r;
      forward += f;
    }
    if (!right && !forward) return;
    // The azimuth still has a heading when the camera looks straight down.
    const theta = this.controls.getAzimuthalAngle();
    const move = new THREE.Vector3(
      right * Math.cos(theta) - forward * Math.sin(theta),
      0,
      -right * Math.sin(theta) - forward * Math.cos(theta),
    )
      .normalize()
      .multiplyScalar(
        this.camera.position.distanceTo(this.controls.target) * PAN_RATE * dt,
      );
    this.camera.position.add(move);
    this.controls.target.add(move);
  }

  dispose(): void {
    this.running = false;
    for (const dispose of this.disposers) dispose();
    this.controls.dispose();
    this.renderer.dispose();
  }

  load(map: ArenaMap): void {
    this.map = structuredClone(map);
    this.undoStack = [];
    this.selected = null;
    this.rebuild();
    this.onChange();
  }

  select(target: Picked | null): void {
    this.selected = target && 'piece' in target ? target.piece : null;
    this.rebuild();
    this.onChange();
  }

  /** Sets fields of the selected piece; a scale of 1 is the default, so it's dropped. */
  updateSelected(patch: Partial<PlacedPiece>): void {
    const selected = this.selected;
    if (selected === null) return;
    this.edit(() => {
      const piece = Object.assign(this.map.pieces[selected], patch);
      if (piece.scale === 1) delete piece.scale;
    });
  }

  deleteSelected(): void {
    const selected = this.selected;
    if (selected === null) return;
    this.selected = null;
    this.edit(() => this.map.pieces.splice(selected, 1));
  }

  setTool(tool: Tool): void {
    this.tool = tool;
    this.cornerMarkers.visible = tool.kind === 'terrain';
    this.refreshGhost();
    this.onChange();
  }

  setBrush(brush: TerrainBrush): void {
    this.brush = brush;
    this.onChange();
  }

  setRadius(radius: number): void {
    this.radius = Math.max(0, Math.min(6, radius));
    this.refreshGhost();
    this.onChange();
  }

  setStrength(strength: number): void {
    this.strength = Math.max(
      0.05,
      Math.min(2, Math.round(strength * 100) / 100),
    );
    this.onChange();
  }

  setLevel(level: number): void {
    this.level = Math.max(0, Math.min(12, level));
    this.grid.position.y = this.level * LEVEL_HEIGHT + 0.02;
    this.positionGhost();
    this.onChange();
  }

  /** A quarter turn, or an eighth when `fine`. */
  rotate(fine = false): void {
    this.rot = (this.rot + (fine ? 0.5 : 1)) % 4;
    this.positionGhost();
    this.onChange();
  }

  setScale(scale: number): void {
    this.scale = Math.max(0.25, Math.min(4, scale));
    this.positionGhost();
    this.onChange();
  }

  undo(): void {
    const previous = this.undoStack.pop();
    if (!previous) return;
    this.map = JSON.parse(previous);
    if (this.selected !== null && this.selected >= this.map.pieces.length)
      this.selected = null;
    this.rebuild();
    this.onChange();
  }

  private snapshot(): void {
    this.undoStack.push(JSON.stringify(this.map));
    if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
  }

  private edit(change: () => void): void {
    this.snapshot();
    change();
    this.rebuild();
    this.onChange();
  }

  /** A press of the terrain tool: one undo step however long it's dragged. Shift erases. */
  private startStroke(erase: boolean): void {
    const hover = this.hover;
    if (!hover) return;
    this.snapshot();
    this.stroke = {
      brush: erase ? 'erase' : this.brush,
      flatten: this.cornerHeights().get(`${hover.x},${hover.z}`) ?? this.level,
      last: '',
      time: 0,
    };
    this.paint();
  }

  private cornerHeights(): Map<string, number> {
    return new Map(
      (this.map.terrain ?? []).map(([x, z, h]) => [`${x},${z}`, h]),
    );
  }

  /** Applies the stroke's brush to the corners around the hovered one. */
  private paint(): void {
    const stroke = this.stroke;
    const hover = this.hover;
    if (!stroke || !hover) return;
    stroke.last = `${hover.x},${hover.z}`;
    stroke.time = performance.now();
    const before = this.cornerHeights();
    const after = new Map(before);
    const r = this.radius;
    // A square of corners, so even a radius of 1 makes whole cells; full strength in the middle.
    for (let dx = -r; dx <= r; dx++) {
      for (let dz = -r; dz <= r; dz++) {
        const x = hover.x + dx;
        const z = hover.z + dz;
        const key = `${x},${z}`;
        const h = before.get(key);
        const weight = Math.max(0, 1 - Math.hypot(dx, dz) / (r + 1));
        if (stroke.brush === 'raise')
          after.set(key, (h ?? 0) + this.strength * weight);
        if (stroke.brush === 'lower')
          after.set(key, (h ?? 0) - this.strength * weight);
        if (stroke.brush === 'flatten') after.set(key, stroke.flatten);
        if (stroke.brush === 'set') after.set(key, this.level);
        if (stroke.brush === 'erase') after.delete(key);
        if (stroke.brush === 'smooth' && h !== undefined) {
          const around = [
            before.get(`${x + 1},${z}`),
            before.get(`${x - 1},${z}`),
            before.get(`${x},${z + 1}`),
            before.get(`${x},${z - 1}`),
          ].filter((v) => v !== undefined);
          const mean =
            around.reduce((sum, v) => sum + v, h) / (around.length + 1);
          after.set(key, h + (mean - h) * Math.max(weight, 0.5));
        }
      }
    }
    // Floor tiles lie flat at level 0, so terrain meeting one stays there too or it shows a ridge.
    for (const placed of this.map.pieces) {
      if (!FLOORS.has(placed.piece) || placed.level !== 0) continue;
      for (const [dx, dz] of [
        [0, 0],
        [1, 0],
        [0, 1],
        [1, 1],
      ]) {
        const key = `${placed.x + dx},${placed.z + dz}`;
        if (after.has(key)) after.set(key, 0);
      }
    }
    // Clamped to what the map schema accepts.
    this.map.terrain = [...after].map(([key, h]) => {
      const [x, z] = key.split(',').map(Number);
      return [x, z, Math.max(-4, Math.min(16, Math.round(h * 20) / 20))];
    });
    this.rebuildTerrain();
    this.positionGhost();
    this.onChange();
  }

  /** Swaps in a fresh terrain mesh without re-cloning every piece, so painting stays smooth. */
  private rebuildTerrain(): void {
    const built = this.mapObject.children[0];
    if (!built) return;
    const old = built.children.find((o) => o.userData.terrain);
    if (old instanceof THREE.Mesh) {
      built.remove(old);
      old.geometry.dispose();
    }
    if (this.map.terrain?.length) built.add(buildTerrain(this.map));
    if (this.templates) paintGround(this.map, this.templates);
    this.refreshCornerMarkers();
  }

  private refreshCornerMarkers(): void {
    const points = (this.map.terrain ?? []).flatMap(([x, z, h]) => [
      (x - 0.5) * TILE_SIZE,
      h * LEVEL_HEIGHT + 0.05,
      (z - 0.5) * TILE_SIZE,
    ]);
    this.cornerMarkers.geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(points, 3),
    );
    this.cornerMarkers.geometry.computeBoundingSphere();
  }

  /** Click: place the tool at the hovered cell, or erase what's under the pointer. */
  private apply(erase: boolean, e: PointerEvent): void {
    const cell = this.hover;
    if (!cell) return;
    const at = (p: { x: number; z: number; level: number }) =>
      p.x === cell.x && p.z === cell.z && p.level === this.level;
    const tool = this.tool;
    if (tool.kind === 'terrain' || tool.kind === 'select') return;
    if (erase || tool.kind === 'erase') {
      const target = this.pick(e);
      if (!target) return;
      this.selected = null;
      this.edit(() => {
        if ('spawn' in target) this.map.spawns.splice(target.spawn, 1);
        else if ('dummy' in target) this.map.dummies?.splice(target.dummy, 1);
        else this.map.pieces.splice(target.piece, 1);
      });
      return;
    }
    if (tool.kind === 'dummy') {
      const level = this.hoverLevel();
      this.edit(() => {
        const dummies = (this.map.dummies ?? []).filter(
          (d) => d.x !== cell.x || d.z !== cell.z,
        );
        if (dummies.length >= MAX_DUMMIES) dummies.shift();
        this.map.dummies = [...dummies, { x: cell.x, z: cell.z, level }];
      });
      return;
    }
    if (tool.kind === 'spawn') {
      this.edit(() => {
        this.map.spawns = this.map.spawns.filter((s) => !at(s));
        const mine = this.map.spawns.filter((s) => s.team === tool.team);
        if (mine.length >= MAX_SPAWNS_PER_TEAM)
          this.map.spawns.splice(this.map.spawns.indexOf(mine[0]), 1);
        this.map.spawns.push({
          team: tool.team,
          x: cell.x,
          z: cell.z,
          level: this.level,
        });
      });
      return;
    }
    const placed: PlacedPiece = {
      piece: tool.piece,
      x: cell.x,
      z: cell.z,
      level: this.level,
      rot: this.rot,
      ...(this.scale !== 1 && { scale: this.scale }),
    };
    this.edit(() => {
      this.map.pieces = this.map.pieces.filter(
        (p) => !(at(p) && p.piece === placed.piece),
      );
      this.map.pieces.push(placed);
    });
  }

  private pointerRay(e: PointerEvent): THREE.Raycaster {
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.camera);
    return ray;
  }

  /** Height in levels of the surface under the pointer. */
  private hoverLevel(): number {
    return Math.round((this.hoverY / LEVEL_HEIGHT) * 100) / 100;
  }

  /** The nearest piece, spawn or dummy drawn under the pointer, at any position or height. */
  private pick(e: PointerEvent): Picked | null {
    const hits = this.pointerRay(e).intersectObjects(
      [this.spawnObject, this.mapObject],
      true,
    );
    for (
      let o: THREE.Object3D | null = hits[0]?.object ?? null;
      o;
      o = o.parent
    ) {
      if (o.userData.index !== undefined) return { piece: o.userData.index };
      if (o.userData.spawn !== undefined) return { spawn: o.userData.spawn };
      if (o.userData.dummy !== undefined) return { dummy: o.userData.dummy };
    }
    return null;
  }

  private updateHover(e: PointerEvent): void {
    const ray = this.pointerRay(e);
    const plane = new THREE.Plane(
      new THREE.Vector3(0, 1, 0),
      -this.level * LEVEL_HEIGHT,
    );
    // The terrain brush aims at the ground you see, and a dummy at the floor you see (pointing at a
    // wall or steep slope places none); over empty space both fall back to the level's plane.
    const terrain = this.mapObject.children[0]?.children.find(
      (o) => o.userData.terrain,
    );
    const seen =
      this.tool.kind === 'terrain' && terrain
        ? ray.intersectObject(terrain)[0]
        : this.tool.kind === 'dummy'
          ? ray.intersectObject(this.mapObject, true)[0]
          : undefined;
    const up = seen?.face?.normal
      .clone()
      .transformDirection(seen.object.matrixWorld);
    const floor = this.tool.kind !== 'dummy' || Math.abs(up?.y ?? 0) > 0.9;
    const point = seen
      ? floor
        ? seen.point
        : null
      : ray.ray.intersectPlane(plane, new THREE.Vector3());
    if (point) this.hoverY = point.y;
    // Terrain edits the nearest cell corner; a dummy stands where you point; Alt snaps pieces to half tiles.
    const snap = (value: number) =>
      this.tool.kind === 'terrain'
        ? Math.round(value / TILE_SIZE + 0.5)
        : this.tool.kind === 'dummy'
          ? Math.round((value / TILE_SIZE) * 20) / 20
          : e.altKey
            ? Math.round((value / TILE_SIZE) * 2) / 2
            : Math.round(value / TILE_SIZE);
    this.hover = point ? { x: snap(point.x), z: snap(point.z) } : null;
    this.positionGhost();
  }

  private rebuild(): void {
    if (!this.templates) return;
    this.environment?.dispose();
    this.environment = new Environment(this.renderer, this.scene, this.map);
    this.mapObject.clear();
    const built = buildMapObject(this.map, this.templates);
    this.mapObject.add(built);
    const selected = built.children.find(
      (o) => o.userData.index === this.selected,
    );
    if (selected) this.highlight.setFromObject(selected);
    this.highlight.visible = selected !== undefined;
    this.spawnObject.clear();
    const marker = (
      at: { x: number; z: number; level: number },
      color: number,
      userData: Record<string, number>,
    ) => {
      const mesh = new THREE.Mesh(
        new THREE.CylinderGeometry(0.5, 0.5, 1.5, 16),
        new THREE.MeshStandardMaterial({
          color,
          transparent: true,
          opacity: 0.8,
        }),
      );
      const origin = pieceOrigin(at);
      mesh.position.set(origin.x, origin.y + 0.75, origin.z);
      mesh.userData = userData;
      this.spawnObject.add(mesh);
    };
    for (const [spawn, at] of this.map.spawns.entries())
      marker(at, TEAM_COLORS[at.team], { spawn });
    for (const [dummy, at] of (this.map.dummies ?? []).entries())
      marker(at, DUMMY_COLOR, { dummy });
    this.refreshCornerMarkers();
  }

  private refreshGhost(): void {
    if (this.ghost) this.scene.remove(this.ghost);
    this.ghost = null;
    if (!this.templates) return;
    const tool = this.tool;
    if (tool.kind === 'select') return;
    if (tool.kind === 'terrain') {
      // A dot on the centre corner and a square around the cells the brush reaches.
      const half = (this.radius + 0.5) * TILE_SIZE;
      const outline = new THREE.LineLoop(
        new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(-half, 0, -half),
          new THREE.Vector3(half, 0, -half),
          new THREE.Vector3(half, 0, half),
          new THREE.Vector3(-half, 0, half),
        ]),
        new THREE.LineBasicMaterial({ color: 0xfff1a8 }),
      );
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.35),
        new THREE.MeshBasicMaterial({ color: 0xfff1a8 }),
      );
      this.ghost = new THREE.Group().add(outline, dot);
    } else if (tool.kind === 'piece') {
      this.ghost = this.templates[tool.piece]!.clone();
      this.ghost.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        const material = (child.material as THREE.MeshStandardMaterial).clone();
        material.transparent = true;
        material.opacity = 0.55;
        child.material = material;
      });
    } else {
      this.ghost = new THREE.Mesh(
        tool.kind === 'erase'
          ? new THREE.BoxGeometry(TILE_SIZE, 0.2, TILE_SIZE)
          : new THREE.CylinderGeometry(0.5, 0.5, 1.5, 16),
        new THREE.MeshBasicMaterial({
          color:
            tool.kind === 'spawn'
              ? TEAM_COLORS[tool.team]
              : tool.kind === 'dummy'
                ? DUMMY_COLOR
                : 0xff3a2a,
          transparent: true,
          opacity: 0.5,
        }),
      );
    }
    this.scene.add(this.ghost);
    this.positionGhost();
  }

  private positionGhost(): void {
    if (!this.ghost) return;
    this.ghost.visible = this.hover !== null;
    if (!this.hover) return;
    const placed = { ...this.hover, level: this.level, rot: this.rot };
    if (this.tool.kind === 'piece') {
      placePiece(this.ghost, {
        ...placed,
        piece: this.tool.piece,
        scale: this.scale,
      });
    } else if (this.tool.kind === 'terrain') {
      const height = this.cornerHeights().get(
        `${this.hover.x},${this.hover.z}`,
      );
      this.ghost.position.set(
        (this.hover.x - 0.5) * TILE_SIZE,
        (height === undefined ? this.hoverY : height * LEVEL_HEIGHT) + 0.05,
        (this.hover.z - 0.5) * TILE_SIZE,
      );
    } else {
      const level = this.tool.kind === 'dummy' ? this.hoverLevel() : this.level;
      const origin = pieceOrigin({ ...this.hover, level });
      this.ghost.position.set(
        origin.x,
        origin.y + (this.tool.kind === 'erase' ? 0.1 : 0.75),
        origin.z,
      );
    }
  }

  private resize(): void {
    const parent = this.canvas.parentElement!;
    this.renderer.setSize(parent.clientWidth, parent.clientHeight, false);
    this.camera.aspect = parent.clientWidth / Math.max(1, parent.clientHeight);
    this.camera.updateProjectionMatrix();
  }
}
